import type { Address, CommercialEntity, RoleTypes, SignerRoleTypes } from './application-service.domain';
import type { BusinessNumberSearchValue } from './component/business-number-search-component.domain';

export type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonValue[] | JsonObject;
export interface JsonObject {
  [key: string]: JsonValue | undefined;
}

export interface ApplicationAddressJson extends Address, JsonObject {
  UnitNumber?: string;
  StreetNumber: string;
  StreetName: string;
  StreetType: string;
  Suburb: string;
  State: string;
  Postcode: string;
  UnformattedAddress: string;
  RawAddress?: string;
  IsManualInput: boolean;
  AreaCode?: string;
  Property?: string;
}

export interface ApplicationIndividualAssetJson extends JsonObject {
  AssetType: string;
  Value: string | number;
  Address?: ApplicationAddressJson;
  Description?: string;
}

export interface ApplicationIndividualLiabilityJson extends JsonObject {
  Type: string;
  Value: string | number;
  Address?: ApplicationAddressJson;
  Financier?: string;
}

// Entity-role flags (per individual x entity). isApplicant is NOT here - it is the entity-less
// consumer/individual borrower and lives on ApplicationIndividual.
export interface ApplicationIndividualRoleFlags {
  isDirector: boolean;
  isPartner: boolean;
  isShareholder: boolean;
  isSoleTrader: boolean;
  isTrustee: boolean;
  isSpouse: boolean;
}

export interface ApplicationAddressRow {
  id?: number;
  applicationId: number;
  unitNumber?: string | null;
  streetNumber?: string | null;
  streetName?: string | null;
  streetType?: string | null;
  suburb?: string | null;
  state?: string | null;
  postcode?: string | null;
  unformattedAddress?: string | null;
  isManualInput?: boolean | null;
  source?: string | null;
  createdAt?: string | Date | null;
  lastUpdate?: string | Date | null;
}

// Employment / consumer-finance block stored as a single JSON column on
// ApplicationIndividual (consumerData). Keys mirror the 5.8.0-03 backfill payload.
export interface ApplicationConsumerData {
  income?: number | null;
  incomeFrequency?: string | null;
  employmentStatus?: string | null;
  employer?: string | null;
  employerContact?: string | null;
  timeAtAddressMonths?: number | null;
  timeAtCurrentEmploymentMonths?: number | null;
  numberOfDependants?: number | null;
  maritalStatus?: string | null;
  previousEmployer?: string | null;
  previousEmployerContact?: string | null;
}

export interface ApplicationIndividualRow {
  applicationId: number;
  id?: number;
  legacyId: string;
  sortOrder: number;
  sfContactId?: string | null;
  title?: string | null;
  givenName?: string | null;
  middleName?: string | null;
  surname?: string | null;
  dateOfBirth?: string | Date | null;
  gender?: string | null;
  email?: string | null;
  mobileNumber?: string | null;
  homeAddressId?: number | null;
  propertyOwnedAddressId?: number | null;
  previousAddressId?: number | null;
  identificationType?: string | null;
  identificationNumber?: string | null;
  driverLicenseState?: string | null;
  driverLicenseExpiry?: string | null;
  hasAlias?: boolean | null;
  aliasNames?: string | null;
  countryOfCitizenship?: string | null;
  isGuarantor: boolean;
  // Per-individual (application-level) flags. isApplicant = the entity-less consumer/individual
  // borrower (Role 'Applicant'); isPrimaryContact = the designated primary contact.
  isApplicant: boolean;
  isPrimaryContact: boolean;
  propertyOwnerFlag?: boolean | null;
  isDeleted?: boolean | null;
  propertyValue?: number | null;
  mortgageBalance?: number | null;
  consumerData?: ApplicationConsumerData | null;
  source?: string | null;
  // Sticky flag raised when the row is created knowing less than a whole individual, cleared once
  // the record is complete. See isIncompleteOnArrivalSource.
  needsAttention?: boolean | null;
  // When the individual form was first saved for this row. Null means never, which is the only
  // state in which the individuals table declines to expand the row.
  detailsSavedAt?: string | Date | null;
  // Retention-only column from 5.9.0-04 (renamed from privacyConsent). Not written by the party
  // save; read for AZ-1629 credit grandfather.
  legacyPrivacyConsent?: boolean | null;
  createdAt?: string | Date | null;
  lastUpdate?: string | Date | null;
  // Transient (hydrated on load / built on save) - not columns on ApplicationIndividual.
  homeAddress?: ApplicationAddressRow | null;
  propertyOwnedAddress?: ApplicationAddressRow | null;
  previousAddress?: ApplicationAddressRow | null;
  isNameOrDobChanged?: boolean | null;
}

export interface ApplicationCommercialEntityRow {
  applicationId: number;
  id?: number;
  matchKey?: string;
  apiType?: string | null;
  sortOrder: number;
  legalName?: string | null;
  tradingName?: string | null;
  abn?: string | null;
  acn?: string | null;
  entityType?: CommercialEntity['EntityType'] | null;
  isBorrower: boolean;
  isPrimary: boolean;
  isGuarantor: boolean;
  isManualKybMode?: boolean | null;
  source?: string | null;
  isDeleted?: boolean | null;
  manualEntry?: boolean | null;
  abnAgeMonths?: number | null;
  phoneNumber?: string | null;
  areaCode?: string | null;
  revenue?: number | null;
  operateAtCommercialAddressFlag?: boolean | null;
  businessCategory?: string | null;
  principalPlaceOfBusinessAddressId?: number | null;
  // Registered office as published by the registry, prefilled from the KYB run but broker-editable.
  registeredBusinessAddressId?: number | null;
  industryClassCode?: string | null;
  // Legacy 3-digit group fallback (FK to IndustryGroup.code) for entities with no 4-digit class.
  industryGroupCode?: string | null;
  industryDetails?: ApplicationCommercialEntityIndustryDetails;
  createdAt?: string | Date | null;
  lastUpdate?: string | Date | null;
  // Transient (hydrated on load / built on save) - not a column on ApplicationCommercialEntity.
  principalPlaceOfBusinessAddress?: ApplicationAddressRow | null;
  registeredBusinessAddress?: ApplicationAddressRow | null;
}

export interface ApplicationCommercialEntityIndustryDetails {
  primaryIndustry?: string | null;
  industrySector?: string | null;
  industryClass?: string | null;
  subdividionClass?: string | null;
  industryDivision?: JsonObject | null;
  industryGroup?: JsonObject | null;
  industryClassDetail?: JsonObject | null;
}

export interface ApplicationIndividualRoleRow extends ApplicationIndividualRoleFlags {
  id?: number;
  applicationId: number;
  individualId?: number;
  legacyIndividualId?: string;
  commercialEntityId?: number | null;
  commercialEntityMatchKey?: string;
  // KYB stakeholder (ApplicationKYBStakeholder.id) this role was materialised from, if any.
  // Null for manually added parties, applicants and third-party guarantors.
  applicationKybStakeholderId?: number | null;
  // The broker created this entity relationship through the manual KYB fallback. This remains true
  // after staff links the role to a later FrankieOne stakeholder.
  isManualKybEntry?: boolean;
  isSigner: boolean;
  // Transient ordering helper only - ApplicationIndividualRole has no sortOrder column.
  sortOrder: number;
  isDeleted?: boolean | null;
  createdAt?: string | Date | null;
  lastUpdate?: string | Date | null;
}

export interface ApplicationEntityRoleRow {
  id?: number;
  applicationId: number;
  commercialEntityId?: number;
  commercialEntityMatchKey?: string;
  ownerEntityId?: number;
  ownerEntityMatchKey?: string;
  isTrustee: boolean;
  isPartner: boolean;
  isDeleted?: boolean | null;
  createdAt?: string | Date | null;
  lastUpdate?: string | Date | null;
}

export interface ApplicationPartiesSnapshot {
  applicationId: number;
  individuals: ApplicationIndividualRow[];
  commercialEntities: ApplicationCommercialEntityRow[];
  individualRoles: ApplicationIndividualRoleRow[];
  entityRoles: ApplicationEntityRoleRow[];
}

/**
 * A role row paired with the entity it is held against. A null entity is an application-level role
 * with no specific entity - the consumer applicant, or a personal guarantor on an application that
 * has no borrower entity.
 */
export interface ApplicationPartyRoleView {
  role: ApplicationIndividualRoleRow;
  entity: ApplicationCommercialEntityRow | null;
}

/**
 * An individual with the individual x entity junction already resolved, so consumers do not each
 * re-scan the sibling arrays of ApplicationPartiesSnapshot. Soft-deleted roles are excluded.
 */
export interface ApplicationIndividualView {
  individual: ApplicationIndividualRow;
  /**
   * The role against the primary entity, else the entity-less application-level role. Deliberately
   * never an arbitrary first role: a corporate-guarantor link promoted here would misreport the
   * individual's headline Role and SignerRole.
   */
  primaryRole: ApplicationPartyRoleView | null;
  /** Corporate guarantor relationships: the entity-linked roles other than the primary one. */
  additionalRoles: ApplicationPartyRoleView[];
}

export interface ApplicationPartiesView {
  /** The borrower entity. Null is a legitimate state, not an error - see primaryEntityRow. */
  primaryEntity: ApplicationCommercialEntityRow | null;
  /** Ordered by sortOrder, matching the legacy array order. */
  individuals: ApplicationIndividualView[];
}

// ---------------------------------------------------------------------------
// The API read shape (AZ-1475). Camel-cased to match the row types, and lossless where the single
// role string it replaced was not: ApplicationIndividualRoleRow carries six independent role flags
// and a KYB stakeholder can hold several at once (director and shareholder, say), where one string
// could only name the highest-precedence of them.
// ---------------------------------------------------------------------------

/** An address as the API returns it: the stored row without its database bookkeeping. */
export type ApplicationAddressApi = Omit<
  ApplicationAddressRow,
  'id' | 'applicationId' | 'source' | 'createdAt' | 'lastUpdate'
>;

/**
 * One individual x entity relationship. The role flags are independent and several may be true at
 * once, which is exactly what a single legacy Role string could not express.
 */
export interface ApplicationIndividualRole extends ApplicationIndividualRoleFlags {
  /**
   * Null for an application-level role holding no entity: the consumer applicant, or a personal
   * guarantor on an application with no borrower entity.
   */
  entityId: number | null;
  entityLegalName?: string | null;
  /**
   * The linked entity's trading name and identifiers, denormalised beside its legal name for the
   * same reason: a consumer holding roles without the entity list cannot look them up. The
   * corporate-guarantor form control and the Docusign signer payload are both business-search
   * values keyed on ABN/ACN, and the server drops a corporate-guarantor role carrying neither.
   */
  entityTradingName?: string | null;
  entityAbn?: string | null;
  entityAcn?: string | null;
  /**
   * Whether the linked entity guarantees the application, denormalised for the same reason as the
   * identifiers above.
   *
   * Without it a consumer holding only roles cannot tell a guarantee from any other link to a
   * non-primary entity - a shareholding held against the corporate trustee reads identically - and
   * the Docusign signer list is built from exactly that distinction.
   */
  entityIsGuarantor?: boolean;
  /**
   * Whether the linked entity borrows: the primary entity, or a corporate trustee/partner of it.
   * Signing against such an entity is signing for the borrower, so individualSignerRole counts it.
   */
  entityIsBorrower?: boolean;
  isSigner: boolean;
  /**
   * The headline relationship: the role against the primary entity, else the entity-less
   * application-level one. At most one role per individual has it, and an individual whose only
   * links are corporate-guarantor ones has none.
   *
   * Published because the server already decides this (ApplicationIndividualView.primaryRole) and a
   * consumer cannot re-derive it from the flags alone. Without it, reproducing the legacy single
   * Role string means relying on array position, which is not a contract.
   */
  isPrimaryRole: boolean;
}

export interface ApplicationIndividual {
  /** ApplicationIndividual.id. Save payloads key on this. */
  id: number;
  /**
   * The stable public identifier, stored as the misleadingly named legacyId column. Not legacy:
   * other tables use it as a foreign key and it is exposed to KYB providers.
   */
  guid: string;
  sfContactId?: string | null;

  title?: string | null;
  givenName?: string | null;
  middleName?: string | null;
  surname?: string | null;
  /** ISO 8601 date. */
  dateOfBirth?: string | null;
  gender?: string | null;
  email?: string | null;
  mobileNumber?: string | null;

  hasAlias?: boolean | null;
  aliasNames?: string | null;
  countryOfCitizenship?: string | null;
  identification?: { type?: string | null; number?: string | null } | null;
  driverLicence?: { state?: string | null; expiry?: string | null } | null;

  // Nested on both read and write. The legacy pair nests the read (HomeAddress) but flattens the
  // write (streetName, propertyOwnedStreetName, ...), so callers translate twice.
  homeAddress?: ApplicationAddressApi | null;
  previousAddress?: ApplicationAddressApi | null;

  /** The entity-less individual borrower - the legacy Role 'Applicant'. */
  isApplicant: boolean;
  isPrimaryContact: boolean;
  isGuarantor: boolean;
  isDeleted?: boolean;

  /** Every live relationship held. Replaces Role, SignerRole and additionalRoles together. */
  roles: ApplicationIndividualRole[];

  /** Asked of guarantors as well as consumer applicants, so always present. */
  property: {
    isOwner: boolean;
    value?: number | null;
    mortgageBalance?: number | null;
    address?: ApplicationAddressApi | null;
  };

  /**
   * Consumer applications only. Absent on commercial ones, rather than a dozen empty top-level
   * fields as in the legacy shape.
   */
  employment?: ApplicationConsumerData | null;

  // Read-only server projections; never written back.
  source?: string | null;
  needsAttention?: boolean;
  detailsSaved?: boolean;
  /**
   * The pre-5.9.0 broker privacy-consent checkbox (AZ-1629). Nothing sets the column any more, so a
   * true flag grandfathers an individual past the credit-bureau consent gate. Projected because that
   * gate is the only reader and it has no other way to see it.
   */
  legacyPrivacyConsent?: boolean;
}

/**
 * An entity-to-entity edge: the entity holds this relationship *over* the owner entity. Replaces the
 * legacy Type values 'Trustee' and 'Partner', which conflated "what this entity is" with "what it is
 * related to" in a single string.
 *
 * An array rather than a single edge because nothing in the schema enforces one edge per entity, even
 * though only the first match is read today. Expect at most one in practice.
 */
export interface ApplicationEntityRelationship {
  /** Null when the edge names its owner by matchKey only, i.e. before the owner has been persisted. */
  ownerEntityId: number | null;
  ownerEntityLegalName?: string | null;
  isTrustee: boolean;
  isPartner: boolean;
}

/**
 * The API read shape for a commercial entity. Flags for what the entity *is*, relationships for what
 * it is related to, and no Type string.
 *
 * Dropping Type is what makes this non-lossy in the one direction that matters: any collapse back to
 * a Type string recomputes it from flags and edges and falls through to 'Guarantor', so a stored
 * 'Member' or 'Secondary' reads back as a guarantor. Those values cannot be created any more, so the flags below
 * are a complete description of every entity a live application can hold.
 *
 * isBorrower is computed as the union of the role flags and the stored column, because each misses
 * cases the other catches - a trustee on an application with no primary entity has no edge to read,
 * and the column is a stale 0 on rows saved before it existed. See applicationCommercialEntityToApi.
 */
export interface ApplicationCommercialEntity {
  id: number;
  legalName?: string | null;
  tradingName?: string | null;
  abn?: string | null;
  acn?: string | null;
  entityType?: CommercialEntity['EntityType'] | null;

  /** The borrower entity. Legitimately false for every entity on an application - see primaryEntityRow. */
  isPrimary: boolean;
  isGuarantor: boolean;
  /**
   * Does this entity borrow at all: the primary entity, or a trustee/partner of a borrowing one -
   * which on a trust or partnership application is a borrower in its own right, not a guarantor.
   * Server-derived; read-only, do not write it back.
   */
  isBorrower: boolean;
  isDeleted?: boolean;

  relationships: ApplicationEntityRelationship[];

  principalPlaceOfBusiness?: ApplicationAddressApi | null;
  registeredBusinessAddress?: ApplicationAddressApi | null;

  phoneNumber?: string | null;
  areaCode?: string | null;
  revenue?: number | null;
  abnAgeMonths?: number | null;
  operatesAtCommercialAddress?: boolean | null;
  businessCategory?: string | null;
  industry?: ApplicationCommercialEntityIndustryDetails | null;
  /**
   * The stored 4-digit ANZSIC class code. Published alongside `industry` because that block holds
   * display names the code cannot always be recovered from, so without this a caller reading an
   * entity and saving it back would blank the column the industry read path joins on.
   *
   * industryGroupCode is deliberately not published: the write shape has no field that carries one,
   * so exposing it would imply a round-trip that silently drops it.
   */
  industryClassCode?: string | null;
  source?: string | null;
  manualEntry?: boolean | null;
}

/** The POST /api2/get-application-parties payload. Shared so both sides of the wire agree. */
export interface GetApplicationPartiesPayload {
  individuals: ApplicationIndividual[];
  commercialEntities: ApplicationCommercialEntity[];
}

// ---------------------------------------------------------------------------
// The API write shape (AZ-1475 Phase 4). The inverse of the read shape above, so a caller can save
// what it read without translating vocabularies twice.
//
// Arrives as SaveApplicationData.parties and goes straight to saveApplicationParties. Reconciliation
// - matching a payload entry to a stored row, soft-deleting by absence, leaving KYB-owned parties
// alone - happens there.
//
// Full-sync, like the legacy arrays it replaces: the payload is the whole set of parties the sender
// owns and one that is absent is soft-deleted. That is why it enters through update-application2
// rather than save-application-parties, whose contract is the opposite - named-only removal
// restricted to internal staff, and consent side effects that must not fire mid-draft.
// ---------------------------------------------------------------------------

/**
 * An individual as a wizard saves it. Drops the read shape's server-derived fields, and keys on `guid`
 * rather than `id` because a party being added has neither until the server assigns one.
 */
export interface ApplicationIndividualWrite extends Omit<
  ApplicationIndividual,
  'id' | 'guid' | 'roles' | 'source' | 'needsAttention' | 'detailsSaved' | 'property'
> {
  /** Absent for a person being added, whose guid the server generates. */
  guid?: string;
  /**
   * isOwner is nullable here where the read shape reports false, because the column keeps the
   * distinction: a payload that never captured property ownership stores null, not "does not own".
   * No reader tells them apart today - both projections coerce to false - but writing a false would
   * still overwrite stored nulls, and the credit rules read this field.
   */
  property: Omit<ApplicationIndividual['property'], 'isOwner'> & { isOwner: boolean | null };
  /**
   * The relationship to the borrower entity, or none - a personal guarantor who is not also a
   * director, say.
   *
   * One role rather than the read shape's array, because a write describes an individual's
   * relationship to the one borrower entity; a role against any other entity is an additionalRole.
   *
   * Several flags may be set within it - a director who is also a shareholder - and both are
   * persisted, since the role row carries them independently. Only a caller that converts to the
   * legacy `Role` string on the way loses the extras, because that string holds one.
   */
  role?: ApplicationIndividualRoleFlags | null;
  /** Signs the documents. Combines with isGuarantor to produce the legacy SignerRole. */
  isSigner: boolean;
  /**
   * Corporate-guarantor links: guarantor entities this individual signs for, other than the borrower.
   * `role` above describes only the relationship to the borrower entity, so these have nowhere else to
   * go - and because the save is a full sync of the role rows, a payload that omits them does not
   * merely fail to add them, it soft-deletes the ones already stored.
   *
   * The entity is named by its identifiers rather than its id: one added in the same payload has no id
   * yet, and the matcher resolves abn, then acn, then legal name.
   */
  additionalRoles?: ApplicationIndividualAdditionalRoleWrite[];
}

/** A link to an entity other than the borrower, as a caller writes it. */
export interface ApplicationIndividualAdditionalRoleWrite {
  business: BusinessNumberSearchValue;
  /**
   * @deprecated Decides nothing. No reader in this repo consults it: the save path ignores it, and
   * the Docusign signer expansion takes only `business`. `isSigner` below carries the one fact it
   * encoded, and `flags` carries what it could never say.
   *
   * Optional rather than removed so a legacy payload still crosses this shape unchanged on its way
   * back out - applicationIndividualFromApi fills 'Guarantor' when a caller omits it, which is what
   * the read direction has always emitted for one of these. New callers leave it alone.
   */
  signerRole?: SignerRoleTypes;
  /**
   * What the individual is to that entity. Optional because the legacy pair above cannot say: it
   * describes a corporate guarantee and nothing else, which is why a shareholding in the corporate
   * trustee had no way to be written here.
   *
   * Stating it also withdraws the guarantee this shape otherwise implies - the entity is no longer
   * inferred to be a guarantor, since an entity that guarantees says so on its own payload entry.
   *
   * Omitted keeps the old reading, so a caller that has nothing to say about the flags leaves the
   * stored ones alone rather than clearing them.
   */
  flags?: ApplicationIndividualRoleFlags;
  /** Whether the individual signs for that entity. Omitted keeps what is stored, else signs. */
  isSigner?: boolean;
}

/**
 * What an entity is related to. `ownerEntityId` is omitted by the wizards, whose trustees and partners
 * always belong to the borrower; a read-modify-write carries it so a trustee of a guarantor trust keeps
 * its owner rather than being re-pointed at the borrower.
 */
export type ApplicationEntityRelationshipWrite = Pick<ApplicationEntityRelationship, 'isTrustee' | 'isPartner'>
  & Partial<Pick<ApplicationEntityRelationship, 'ownerEntityId'>>;

/**
 * A commercial entity as a wizard saves it. `isBorrower` is absent because the server derives it, and
 * `id` because a new entity has none.
 */
export interface ApplicationCommercialEntityWrite extends Omit<
  ApplicationCommercialEntity,
  'id' | 'isBorrower' | 'relationships' | 'source'
> {
  id?: number;
  /**
   * Only the first is read, matching commercialEntityRoleType: what reaches the row is a single
   * legacy Type string, which can say 'Trustee' or 'Partner' but not both.
   */
  relationships?: ApplicationEntityRelationshipWrite[];
  /**
   * The 4-digit ANZSIC class code the row stores and the industry read path joins on. Carried
   * explicitly because `industry` holds display names a code cannot always be recovered from, so a
   * read-modify-write echoing only `industry` back would blank the stored code.
   *
   * There is deliberately no industryGroupCode counterpart: the legacy input has no field that
   * carries one, and inventing a fallback here would make this path save something the current one
   * does not.
   */
  industryClassCode?: string | null;
}

/**
 * The party half of an update-application2 payload, in the API shape.
 *
 * An absent array means "not part of this update" and leaves those parties untouched, matching the
 * legacy arrays. An empty array is a full sync to nothing, which removes them.
 */
export interface ApplicationPartiesWrite {
  individuals?: ApplicationIndividualWrite[];
  commercialEntities?: ApplicationCommercialEntityWrite[];
}

export type ApplicationPartyRoleString = Exclude<RoleTypes, null>;

export interface MergeApplicationIndividualsInput {
  applicationId: number;
  survivorIndividualId: number;
  mergedIndividualIds: number[];
}

export interface MergeApplicationIndividualsResult {
  survivorIndividualId: number;
  mergedIndividualIds: number[];
  /** Follow-ups after the merge committed that failed (note, DocuSign void, document metadata). */
  warnings: string[];
}
